/**
 * DASHBOARD-RESULTS-TOPICS-ANIMATED.WIDGET
 * Rosace "tree with topics scores" — même géométrie (clip-path + rotations) que
 * dashboard-results-topics.widget.tsx, laissé intact en référence/fallback,
 * mais avec une couche d'animation par-dessus (entrée en éventail, score qui
 * compte, hover discret).
 *
 * Piège évité : appliquer scale/rotate via framer-motion sur un wrapper qui a
 * des enfants position:absolute casserait leur positionnement (un transform
 * crée un nouveau containing block). Ici chaque pétale/pastille est déjà une
 * feuille sans enfant absolu, donc animer scale dessus est sans risque — la
 * rotation, elle, est passée en prop numérique `rotate` (pas une string
 * transform) pour que framer-motion la fusionne proprement avec le scale animé.
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Session } from "@/redux/_session.types"
import { Topic } from "@/redux/topic.types"
import Plot from "react-plotly.js"
import { PLOTLY_HOVERLABEL } from "@/utils/plotly-hover.utils"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCloudSun } from "@fortawesome/free-solid-svg-icons"
import ReactTooltip from "react-tooltip"
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import { uniq } from "lodash"
import { useCountUp } from "@/utils/use-count-up.utils"
import Skeleton, { useSkeletonPulse } from "@/components/skeleton"

interface StateProps extends WithTranslation {
  _session: Session
}

interface OwnProps {
  currentTopics: Topic[]
  note: number
  isPreview?: boolean
  isSkeleton?: boolean //Rend la rosace en mode gris pulsé (placeholder de chargement) au lieu des vraies données
}

//Notes variées (échelle brute 0-100) pour un anneau au look moins mécanique qu'un dégradé uniforme
//Attention : ne pas passer `axisColor`/`label` au constructeur, ce sont des getters sans setter
//sur Topic (Object.assign lèverait une TypeError) — la couleur/le libellé sont court-circuités
//plus bas dans getTopicBack/le rendu quand isSkeleton est actif.
//Tuilé sur ~30 pétales pour retrouver la densité fine de la vraie rosace (10 aurait donné des
//pétales bien plus larges que la réalité)
const SKELETON_NOTES_BASE = [62, 78, 45, 85, 55, 70, 40, 88, 60, 50]
const SKELETON_NOTES = Array.from({ length: 30 }, (_, i) => SKELETON_NOTES_BASE[i % SKELETON_NOTES_BASE.length])
const SKELETON_TOPICS: Topic[] = SKELETON_NOTES.map((note, i) => new Topic({ id: "skeleton-" + i, note, hide: false }))

type Props = StateProps & OwnProps

const SIZE: number = 340

//Customer with bug => switch display
//List users ide
const IS_PLOTLY_CUSTOMERS: string[] = [
  "ec36061a-5485-4713-81c6-f862a6c6e302" //Benjamin Pavageau
]

//Rotate border according number of questions
const ROTATION_DIFF: any = {
  4: -36,
  5: -18,
  6: -6,
  7: 2,
  8: 8,
  9: 13,
  10: 17,
  11: 20,
  12: 23,
  13: 26,
  14: 28,
  15: 29,
  16: 31,
  17: 32,
  18: 34,
  19: 34.5,
  20: 35.5,
  21: 36,
  22: 37,
  23: 37.5,
  24: 38,
  25: 39,
  26: 39.5,
  27: 39.5,
  28: 40.5,
  29: 41,
  30: 41,
  31: 42,
  32: 42,
  33: 42,
  34: 42,
  35: 42.5,
  36: 42.5,
  37: 43.5,
  38: 44,
  39: 44,
  40: 44.2,
  41: 44.5,
  42: 44.5,
  43: 45,
  44: 45,
  45: 45.5,
  46: 45.5,
  47: 46,
  48: 46,
  49: 46,
  50: 46,
  51: 46,
  52: 46,
  53: 46,
  54: 46,
  55: 46,
  56: 46,
  57: 46,
  58: 46,
  59: 46,
  60: 46,
  61: 46,
  62: 46,
  63: 46,
  64: 46,
  65: 46,
  66: 46,
  67: 46,
  68: 46,
  69: 46,
  70: 46
}

//Entrée légère : les pétales apparaissent en douceur (fondu + très léger zoom, sans effet de ressort) avec un
//petit décalage en éventail, plafonné pour que les comptes avec beaucoup de topics n'aient pas une entrée interminable.
//Durée totale : environ 0,5 s
const ENTER_EASE = [0.22, 1, 0.36, 1] as const
function enterDelay(i: number): number {
  return Math.min(i, 20) * 0.01
}

//En dessous de ce nombre de topics, les labels ont assez d'écart angulaire entre eux
//pour rester lisibles sans aide ; au-delà, le halo blanc les détache du fond/des voisins
const HALO_TOPICS_THRESHOLD = 15

function DashboardResultsTopicsAnimatedWidget(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  //Entrée en éventail + score qui compte : seulement à l'ouverture d'une enquête (épileptique à chaque navigation)
  //Le squelette garde toujours son animation de chargement
  const location = useLocation()
  const isAnimated = props.isSkeleton || location.state?.withAnimation === true

  const [topics, setTopics] = useState<Topic[]>(props.isSkeleton ? SKELETON_TOPICS : props.currentTopics)
  const [isPlotly] = useState(IS_PLOTLY_CUSTOMERS.indexOf(props._session.userId) > -1)
  const [plotlyData, setPlotlyData] = useState<Plotly.Data[]>([])

  //Pulse opacity par pétale (mode skeleton uniquement) — layered au-dessus du scale animé
  //d'entrée, sans conflit puisque framer-motion ne gère ici que `scale`, pas `opacity`
  const { itemStyle: skeletonPulseStyle } = useSkeletonPulse(SKELETON_TOPICS.length)

  //Note to display at the center of the circle
  const [note, setNote] = useState(props.note)

  //Note affichée, animée en tween vers `note` (compteur qui défile plutôt qu'un saut sec)
  const countUpNote = useCountUp(note, { duration: 600 })
  const displayedNote = isAnimated ? countUpNote : note

  //Deg and tang (to calculate radius)
  const [math, setMath] = useState({
    deg: 0,
    degC: 0,
    tan: 0
  })

  useEffect(() => {
    if (props.isSkeleton) return

    if (props.currentTopics.length > 0) {
      setTopics(props.currentTopics)
      setNote(props.note)

      if (isPlotly) {
        const plotlyDataRows: any[] = []

        uniq(props.currentTopics.map((x) => x.AxisId)).forEach((axisId) => {
          const axis = props.currentTopics.find((x) => x.AxisId === axisId)?.Axis
          if (axis) {
            const plotlyDataRow: any = {
              r: [],
              theta: [],
              name: "Relation au manager",
              marker: { color: axis.color },
              type: "barpolar"
            }

            props.currentTopics
              .filter((x) => x.AxisId === axis.id)
              .forEach((topic) => {
                plotlyDataRow.r.push(topic.note)
                plotlyDataRow.theta.push(topic.label)
              })

            plotlyDataRows.unshift(plotlyDataRow)
          }
        })

        setPlotlyData(plotlyDataRows)
      }
    }
  }, [props.currentTopics, props.note])

  useEffect(() => {
    //Math option
    //Detect angle for every branch of the tree
    function calculateMath() {
      const topicsCount = props.isSkeleton ? SKELETON_TOPICS.length : props.currentTopics.length

      if (topicsCount > 1) {
        const deg: number = 360 / topicsCount
        const degC: number = (90 - deg) / 2
        const rad: number = (degC * Math.PI) / 180
        const tanDeg: number = Math.tan(rad)
        const tan: number = 50 * tanDeg

        return {
          deg: deg,
          degC: degC,
          tan: tan
        }
      } else {
        return {
          deg: 0,
          degC: 0,
          tan: 0
        }
      }
    }

    setMath(calculateMath())
  }, [props.currentTopics.length])

  //Click on a topic to scroll at him in the page questions
  function clickTopic(topic) {
    if (!props.isPreview && !props.isSkeleton) {
      navigate("/dashboard/questions?topic_id=" + topic.id)
    }
  }

  //Get background for branch of tree
  //> color depend of the axis or if the nightmare mode is active
  //> rotate est laissé en nombre (deg implicite) au lieu d'une string transform,
  //  pour que framer-motion puisse le fusionner avec le scale animé de l'entrée
  function getTopicBack(topic, i) {
    let note = props._session.dashboardDisplaySettings.dashboardDisplayMode === "note" ? topic.note : topic.satisfaction
    let color = topic.axisColor
    let opacity

    //Color according the nightmare mode
    if (topic.hide) {
      opacity = 1
      color = "white"
      note = 100
    } else {
      if (props._session.dashboardDisplaySettings.dashboardNightmareModeDisplay) {
        if (note <= props._session.dashboardDisplaySettings.dashboardNightmareModeMin) {
          color = props._session.dashboardDisplaySettings.dashboardNightmareColor1
        } else if (note >= props._session.dashboardDisplaySettings.dashboardNightmareModeMax) {
          color = props._session.dashboardDisplaySettings.dashboardNightmareColor3
        } else {
          color = props._session.dashboardDisplaySettings.dashboardNightmareColor2
        }
      } else {
        if (note >= 80) opacity = 0.8
        else if (note >= 70) opacity = 0.7
        else if (note >= 60) opacity = 0.6
        else if (note >= 50) opacity = 0.5
        else opacity = 0.4
      }
    }

    //Force ceil about note
    if (note < 35) note = 35
    if (note >= 90) note = 90

    //Calc size of the card
    const margin = 150 - note * 1.5

    return {
      opacity: props.isSkeleton ? 1 : opacity,
      height: SIZE - margin * 2 + "px",
      width: SIZE - margin * 2 + "px",
      margin: margin + "px",
      borderRadius: (SIZE - margin * 2) / 2 + "px",
      backgroundColor: props.isSkeleton ? "#E5E7EB" : color,
      clipPath: "polygon(50% 50%, 50% 50%, 100% " + (50 - math.tan) + "%, 100% 0%, " + (50 + math.tan) + "% 0%)",
      willChange: "clip-path, transform",
      rotate: i * math.deg - 39
    }
  }

  //Style of the little color dot for a topic
  function getTopicPastille(topic, i) {
    let rotation = i * math.deg - 75.5 + math.degC

    if (topics.length === 5) {
      rotation = rotation + 27
    }

    const style: any = { backgroundColor: topic.AxisId ? topic.axisColor : null }
    if (rotation < 150 || rotation > 340) {
      style.right = "-16px"
    } else {
      style.left = "-16px"
    }

    return style
  }

  //Style of the border
  function getTopicBorder(i) {
    const rotation = i * math.deg - 75.5 + math.degC
    return {
      width: SIZE + "px",
      marginTop: SIZE / 2 - 1 + "px",
      transform: "rotate(" + (rotation - ROTATION_DIFF[topics.length]) + "deg)"
    }
  }

  //Style of the text for all topics
  function getTopicText(topic, i) {
    let rotation = i * math.deg - 75.5 + math.degC

    const style: any = {
      height: SIZE + "px",
      width: SIZE + "px"
    }

    if (topic.hide) {
      style.color = "#dadada"
    }

    if (topics.length < 6) {
      if (topics.length === 4) {
        rotation = rotation + 33.3
      } else if (topics.length === 5) {
        rotation = rotation + 27
      }
    }

    if (rotation < 150 || rotation > 340) {
      style.transform = "rotate(" + (rotation - 48.5) + "deg)"
      style.justifyContent = "flex-end"
    } else {
      style.transform = "rotate(" + (rotation + 133) + "deg)"
    }

    return style
  }

  return (
    <div
      className="results-tree"
      style={{
        height: SIZE + "px",
        width: SIZE + "px",
        margin: SIZE / 10
      }}>
      {isPlotly && (
        <div className="abs" style={{ top: -30, left: -30, transform: "rotate(268deg)" }}>
          <Plot
            layout={{
              hoverlabel: PLOTLY_HOVERLABEL,
              xaxis: { visible: false },
              yaxis: { visible: false },
              margin: { l: 20, r: 20, b: 20, t: 20 },
              height: 400,
              width: 400,
              font: {
                size: 10,
                color: "#111C2B"
              },
              polar: {
                bgcolor: "rgba(0,0,0,0)", // Fond transparent (optionnel)
                radialaxis: { visible: false }, // Cache les grilles
                angularaxis: { visible: false } // Cache les grilles
              },
              showlegend: false
            }}
            data={plotlyData}></Plot>
        </div>
      )}
      <div
        className="results-tree-center"
        style={{
          top: (SIZE - 76) / 2 + "px",
          left: (SIZE - 76) / 2 + "px"
        }}>
        {props.isSkeleton ? (
          <>
            <div className="results-tree-note flex" style={{ justifyContent: "center" }}>
              <Skeleton width={40} height={16} />
            </div>
            <div className="grey-t flex" style={{ justifyContent: "center", marginTop: 6 }}>
              <Skeleton width={28} height={9} />
            </div>
          </>
        ) : (
          <>
            <div className="results-tree-note">
              {note > 0 && props._session.dashboardDisplaySettings.dashboardDisplayMode === "note" ? (
                <b>{(displayedNote / 10)?.toFixed(1)}</b>
              ) : (
                <div className="flex">
                  <Space />
                  <b>{displayedNote?.toFixed(0)}</b>
                  <div
                    style={{
                      fontSize: "18px",
                      marginTop: "4px",
                      marginBottom: "-4px"
                    }}>
                    %
                  </div>
                  <Space />
                </div>
              )}
            </div>

            <div className="grey-t">
              {note > 0 ? (
                <span>
                  {props._session.dashboardDisplaySettings.dashboardDisplayMode === "note"
                    ? t("survey_note")
                    : t("survey_satisfaction")}
                </span>
              ) : (
                <FontAwesomeIcon icon={faCloudSun} style={{ color: "#d4d4d4" }} />
              )}
            </div>
          </>
        )}
      </div>

      {topics.map((topic, i) => (
        <div key={i}>
          {!isPlotly && (
            <motion.div
              style={{ ...getTopicBack(topic, i), ...(props.isSkeleton ? skeletonPulseStyle(i) : undefined) }}
              className="results-tree-back"
              initial={!isAnimated ? false : props.isSkeleton ? { scale: 0.9 } : { scale: 0.88, opacity: 0 }}
              animate={props.isSkeleton ? { scale: 1 } : { scale: 1, opacity: 1 }}
              whileHover={!topic.hide && !props.isSkeleton ? { scale: 1.05, filter: "brightness(1.08)" } : undefined}
              transition={{
                scale: { delay: enterDelay(i), duration: 0.35, ease: ENTER_EASE },
                opacity: { delay: enterDelay(i), duration: 0.25 },
                filter: { duration: 0.15 }
              }}></motion.div>
          )}

          {!props.isSkeleton && (
            <motion.div
              style={getTopicText(topic, i)}
              initial={isAnimated ? { opacity: 0 } : false}
              animate={{ opacity: 1 }}
              transition={{ delay: enterDelay(i), duration: 0.25 }}
              data-tip={
                topic.label +
                " : " +
                (props._session.dashboardDisplaySettings.dashboardDisplayMode === "note"
                  ? topic.noteLabel
                  : topic.satisfaction.toFixed(1) + "%")
              }
              data-for={topic.id}
              className="results-tree-text flex">
              <div style={getTopicPastille(topic, i)} className="results-tree-pastille"></div>

              <span
                onClick={() => clickTopic(topic)}
                className={
                  (!props.isPreview ? "_hover " : "") +
                  (topics.length > HALO_TOPICS_THRESHOLD ? "results-tree-text-halo" : "")
                }>
                {topic.label.length > 20 ? topic.label.slice(0, 20).trimEnd() + "…" : topic.label}
              </span>
            </motion.div>
          )}
          {!props.isPreview && !props.isSkeleton && <ReactTooltip id={topic.id} />}

          {!isPlotly && (
            <div style={getTopicBorder(i)} className="results-tree-border-container flex">
              <div className="flex1"></div>
              <div className="results-tree-border flex1"></div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(DashboardResultsTopicsAnimatedWidget))
